/**
 * entityLinks — Detect Epicor entity references in chat text and convert
 * them into clickable links to the Entity Hub Page.
 *
 * Strategy
 * ────────
 * Pre-process the assistant's markdown BEFORE it reaches react-markdown,
 * wrapping every matched identifier in standard markdown link syntax:
 *
 *   "Customer #247 (PMI) has an open AR balance of $14K"
 *     ↓ linkifyEntities()
 *   "[Customer #247](/knowledge/entity/customer/247) (PMI) has an open AR balance of $14K"
 *
 * The MessageContent component pairs this with an `a` component override
 * that routes entity-hub URLs through React Router's <Link> so clicks do
 * not trigger a full page reload.
 *
 * Patterns
 * ────────
 * Only HIGH-CONFIDENCE explicit-ID forms are matched in V1:
 *   - "Customer #247", "Customer 247", "CustNum 247"  → /customer/247
 *   - "Vendor #1713", "VendorNum 1713"                → /vendor/1713
 *   - "Job 25712"                                     → /job/25712
 *   - "Order #12345", "OrderNum 12345"                → /project/12345
 *
 * Company-name aliases (PMI, Marriott, etc.) are intentionally NOT matched —
 * they false-match too aggressively without context. The numeric form is
 * always present in Trendy's tool output so the link rate stays high.
 *
 * Code-fence safety
 * ─────────────────
 * The linkifier skips content inside triple-backtick fences and inline
 * `code spans` so JSON examples / SQL strings / variable names are not
 * mid-token-rewritten.
 *
 * Already-linked safety
 * ─────────────────────
 * The linkifier skips matches inside existing markdown link text or URL
 * targets `[Customer 247](/x)` and `(/customer/247)` to avoid nesting.
 */

// ── Pattern map ────────────────────────────────────────────────────────────
//
// Order matters: more specific (CustNum) before generic (Customer). The
// regex captures the numeric id in group 1; the entity-type and display
// label are fixed per row. Word-boundaries + lookarounds avoid false hits
// inside larger identifiers like "Customer123ABC".

const PATTERNS = [
  // Customers
  {
    re: /\bCustNum\s+(\d{1,7})\b/g,
    type: 'customer',
    label: (m) => `CustNum ${m[1]}`,
  },
  {
    re: /\bCustomer\s+#\s*(\d{1,7})\b/g,
    type: 'customer',
    label: (m) => `Customer #${m[1]}`,
  },
  {
    re: /\bCustomer\s+(\d{2,7})\b(?!\s*(?:%|st|nd|rd|th))/g,
    type: 'customer',
    label: (m) => `Customer ${m[1]}`,
  },
  // Vendors
  {
    re: /\bVendorNum\s+(\d{1,7})\b/g,
    type: 'vendor',
    label: (m) => `VendorNum ${m[1]}`,
  },
  {
    re: /\bVendor\s+#\s*(\d{1,7})\b/g,
    type: 'vendor',
    label: (m) => `Vendor #${m[1]}`,
  },
  // Jobs — Amtrend job numbers are 4-7 digits, narrower to reduce false hits
  {
    re: /\bJob\s+(\d{4,7})\b/g,
    type: 'job',
    label: (m) => `Job ${m[1]}`,
  },
  // Orders → project hub (Project is a view over OrderHed)
  {
    re: /\bOrderNum\s+(\d{4,8})\b/g,
    type: 'project',
    label: (m) => `OrderNum ${m[1]}`,
  },
  {
    re: /\bOrder\s+#\s*(\d{4,8})\b/g,
    type: 'project',
    label: (m) => `Order #${m[1]}`,
  },
  {
    re: /\bSO\s+(\d{4,8})\b/g,
    type: 'project',
    label: (m) => `SO ${m[1]}`,
  },
]


/**
 * Walk the markdown source and tag every position that should be SKIPPED
 * by linkification: fenced code blocks, inline code spans, existing
 * markdown link targets, and existing markdown link labels.
 *
 * Returns an array of [start, end] half-open ranges to avoid.
 */
function findSkipRanges(text) {
  const skips = []

  // ``` fenced blocks (multi-line)
  const fenceRe = /```[\s\S]*?```/g
  let m
  while ((m = fenceRe.exec(text)) !== null) {
    skips.push([m.index, m.index + m[0].length])
  }

  // `inline code` (single backtick, no newline)
  const inlineRe = /`[^`\n]+`/g
  while ((m = inlineRe.exec(text)) !== null) {
    skips.push([m.index, m.index + m[0].length])
  }

  // Existing markdown links: [label](url) — skip both the label and the url
  const linkRe = /\[([^\]]*)\]\(([^)]*)\)/g
  while ((m = linkRe.exec(text)) !== null) {
    skips.push([m.index, m.index + m[0].length])
  }

  // Markdown image refs ![alt](url)
  const imgRe = /!\[([^\]]*)\]\(([^)]*)\)/g
  while ((m = imgRe.exec(text)) !== null) {
    skips.push([m.index, m.index + m[0].length])
  }

  return skips
}

function isInSkip(start, end, skips) {
  for (const [s, e] of skips) {
    if (start < e && end > s) return true
  }
  return false
}


/**
 * linkifyEntities — main entrypoint.
 *
 * Walks each pattern, replacing matches with [label](path) markdown.
 * Returns the rewritten string. If no matches found, returns the original
 * (referentially equal) so callers can short-circuit re-renders.
 */
export function linkifyEntities(text) {
  if (!text || typeof text !== 'string') return text
  // Quick reject: if no candidate keywords appear at all, skip the work.
  if (!/(Customer|CustNum|Vendor|Job|Order|SO)/i.test(text)) return text

  const skips = findSkipRanges(text)

  // We collect non-overlapping replacements and apply them in one pass
  // (in reverse order so earlier indices stay valid).
  const replacements = []
  const claimed = [] // ranges already claimed by an earlier (more specific) pattern

  for (const { re, type, label } of PATTERNS) {
    re.lastIndex = 0
    let m
    while ((m = re.exec(text)) !== null) {
      const start = m.index
      const end = start + m[0].length
      if (isInSkip(start, end, skips)) continue
      if (isInSkip(start, end, claimed)) continue
      const id = m[1]
      const lbl = label(m)
      const url = `/knowledge/entity/${type}/${encodeURIComponent(id)}`
      replacements.push({ start, end, replacement: `[${lbl}](${url})` })
      claimed.push([start, end])
    }
  }

  if (replacements.length === 0) return text

  // Apply in reverse so indices remain valid
  replacements.sort((a, b) => b.start - a.start)
  let out = text
  for (const r of replacements) {
    out = out.slice(0, r.start) + r.replacement + out.slice(r.end)
  }
  return out
}


/**
 * Test helper: returns the list of (raw matched text, entity url) pairs
 * the linkifier WOULD produce, without mutating the source. Useful for
 * unit tests and debug overlays.
 */
export function detectEntities(text) {
  if (!text || typeof text !== 'string') return []
  const skips = findSkipRanges(text)
  const found = []
  const claimed = []
  for (const { re, type, label } of PATTERNS) {
    re.lastIndex = 0
    let m
    while ((m = re.exec(text)) !== null) {
      const start = m.index
      const end = start + m[0].length
      if (isInSkip(start, end, skips)) continue
      if (isInSkip(start, end, claimed)) continue
      claimed.push([start, end])
      found.push({
        match: m[0],
        label: label(m),
        type,
        id: m[1],
        url: `/knowledge/entity/${type}/${encodeURIComponent(m[1])}`,
      })
    }
  }
  return found
}


/**
 * Predicate used by the chat markdown <a> override to decide whether a
 * given href should be intercepted by React Router rather than a full
 * page navigation.
 */
export function isEntityHubUrl(href) {
  if (!href || typeof href !== 'string') return false
  return href.startsWith('/knowledge/entity/')
}
